import { isAxiosError } from "axios";

import httpClient from "@/api/httpClient";
import { checkIsTokenExpired } from "@/utils/tokenUtils";
import { getUserDataFromLocalStorage, safeLocalStorage } from "@/utils";
import authVersionService from "../services/authVersionService";
import { versionHeaderKeys } from "../constants";

let isRefreshing = false;
let subscribers: ((token: string) => void)[] = [];
let failedRequests: ((error: any) => void)[] = [];

function subscribeTokenRefresh(onSuccess: (token: string) => void, onError: (error: any) => void) {
  subscribers.push(onSuccess);
  failedRequests.push(onError);
}

function onRefreshed(token: string) {
  subscribers.forEach((cb) => cb(token));
  subscribers = [];
  failedRequests = [];
}

function onRefreshFailed(error: any) {
  failedRequests.forEach((cb) => cb(error));
  subscribers = [];
  failedRequests = [];
}

const isAuthRequest = (url = "") => /^\/auth(\/|$)/.test(url);

const isNetworkError = (error: unknown) => isAxiosError(error) && !error.response;

export function setupAuthRequestInterceptor() {
  return httpClient.interceptors.request.use(
    async (config) => {
      // The refresh's own /auth call must not wait for the refresh.
      if (isAuthRequest(config.url)) return config;

      const savedUser = getUserDataFromLocalStorage();
      const token = savedUser?.token;

      if (token && checkIsTokenExpired(token)) {
        if (!isRefreshing) {
          isRefreshing = true;

          authVersionService
            .authenticate()
            .then((data) => {
              const newToken = data.token;
              onRefreshed(newToken);
              return newToken;
            })
            .catch((err) => {
              // Offline after sleep isn't a rejected session: keep it, so the next request refreshes again.
              if (!isNetworkError(err)) {
                authVersionService.clearHeaders();
                safeLocalStorage.removeItem("user");
              }
              onRefreshFailed(err);
            })
            .finally(() => {
              isRefreshing = false;
            });
        }

        return new Promise((resolve) => {
          subscribeTokenRefresh(
            (newToken) => {
              config.headers.set(versionHeaderKeys.default, `Bearer ${newToken}`);
              resolve(config);
            },
            () => {
              resolve(config);
            }
          );
        });
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
}
